// SEN-1417: dashboard MASTER filter — the pure translation layer.
//
// The persisted master filter is declared against a PIVOT TYPE and stored as
// the COMPILED reduce FilterClause wire tree (the same grammar as a widget's
// config.filter — compiled by the ONE strip compile path,
// compileToReduceFilter). Application per widget at request-build time:
//   - widget type === pivot   → the master leaves apply as-is;
//   - widget type has a FORWARD entity-reference whose target is the pivot
//     → every leaf attribute is prefixed `${refSlug}.` (the reduce grammar
//       resolves dot-paths — reduce.py:1624);
//   - no path → NOT APPLIED, and the tile carries a truthful "master filter
//     n/a" badge — never silently unfiltered (the SEN-433 drift class).
// Text widgets are exempt (no data, no badge).
import type { DashboardWidget } from '../api/client'
import type { FilterNode } from '../collection/filter/model'
import type { ReduceRequest, Scope } from '../views/contract'

type GroupNode = Extract<FilterNode, { kind: 'group' }>

// ── Wire tree (reduce FilterClause) ──────────────────────────────────────────
export type WireLeaf = { attribute: string; op: string; value: unknown }
export type WireTree = WireLeaf | { and: WireTree[] } | { or: WireTree[] }

export function isWireLeaf(t: WireTree): t is WireLeaf {
  return typeof (t as WireLeaf).attribute === 'string'
}

/** Backend bound (monad/api/layouts.py MasterFilter._bounded_tree). */
const MAX_TREE_BYTES = 8_192

/** The persisted `tree` jsonb is the wire FilterClause plus one root-level
 *  `ui` key (FilterBar round-trip state — the UI tree carries attribute ids,
 *  hop paths and operator metadata the wire slugs can't reconstruct). Strip
 *  it before the tree touches anything wire-shaped. */
export function stripUi(tree: Record<string, unknown> | null | undefined): WireTree | undefined {
  if (!tree) return undefined
  const { ui: _ui, ...wire } = tree
  if ('and' in wire || 'or' in wire || 'attribute' in wire) return wire as WireTree
  return undefined
}

/** SEN-1763: a null tree, or one that is {} once `ui` is stripped, carries NO
 *  conditions — it is a scoped PORTFOLIO binding, not an undrawable one. The
 *  strip draws it as zero chips; only a tree with content the chips cannot
 *  represent stays read-only. One meaning of "no conditions", both sides. */
export function isEmptyTree(tree: Record<string, unknown> | null | undefined): boolean {
  if (!tree) return true
  const { ui: _ui, ...wire } = tree
  return Object.keys(wire).length === 0
}

/** Attach the UI round-trip state to the compiled wire tree. If the combined
 *  jsonb would exceed the backend's 8KB bound, drop the round-trip state
 *  (the filter still works; the editor reopens blank) rather than 422. */
export function withUi(wire: WireTree, ui: unknown): Record<string, unknown> {
  const full = { ...(wire as Record<string, unknown>), ui }
  // Cold-read H3: measure BYTES (TextEncoder), not UTF-16 units — and the
  // backend now measures the same canonical compact form, so a client-passing
  // payload can't 422 server-side on Danish characters.
  if (new TextEncoder().encode(JSON.stringify(full)).length <= MAX_TREE_BYTES) return full
  return { ...(wire as Record<string, unknown>) }
}

/** Deep-copy a wire tree with every leaf attribute prefixed by the forward
 *  entity-reference slug — "country" becomes "site.country". */
export function prefixLeaves(tree: WireTree, refSlug: string): WireTree {
  if (isWireLeaf(tree)) return { ...tree, attribute: `${refSlug}.${tree.attribute}` }
  if ('and' in tree) return { and: tree.and.map((c) => prefixLeaves(c, refSlug)) }
  return { or: tree.or.map((c) => prefixLeaves(c, refSlug)) }
}

// ── Per-widget application ───────────────────────────────────────────────────
/** The subset of the wire Attribute shape the translation reads. target_type_id
 *  and config ride the schema's .passthrough() — callers cast (the App.tsx
 *  pattern). */
export type WidgetAttr = {
  attribute_id: string
  slug: string
  type: string
  name?: string
  target_type_id?: string | null
  config?: Record<string, unknown> | null
}

/** Reference dims for a widget: bound slug → target type, from the widget
 *  type's own attribute metadata (one hop; widget bindings are slugs) — the
 *  dims whose group keys (record ids) resolve to kennings. One function for
 *  a dashboard tile and a record page's widget. */
export function refDimsFrom(attrs: WidgetAttr[], roleNames: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {}
  for (const slug of Object.values(roleNames)) {
    const a = attrs.find((x) => x.slug === slug)
    if (a?.type === 'entity-reference' && a.target_type_id) out[slug] = a.target_type_id
  }
  return out
}

/** Each bound role's attribute config (roleName → config), from the widget
 *  type's own metadata — the metric's icon and unit (config.icon,
 *  config.unit_label) reach a renderer this way (A8), exactly as App.tsx
 *  hands a view its roleConfigs. Dotted (multi-hop) slugs resolve to none. */
export function roleConfigsFrom(attrs: WidgetAttr[], roleNames: Record<string, string>,
  bind?: { records?: { aggregates?: Array<{ fn: string; attribute: string }> | null } | null } | null,
): Record<string, Record<string, unknown>> {
  const out: Record<string, Record<string, unknown>> = {}
  for (const [role, slug] of Object.entries(roleNames)) {
    const a = slug && !slug.includes('.') ? attrs.find((x) => x.slug === slug) : undefined
    if (a?.config) out[role] = a.config as Record<string, unknown>
  }
  // a records binding's aggregates, keyed as a figure names them ("sum.size_sqm"): the aggregated attribute's
  // served config (its unit, its display format) — so a records figure takes its unit from metadata too
  for (const agg of bind?.records?.aggregates ?? []) {
    const a = agg.attribute && !agg.attribute.includes('.') ? attrs.find((x) => x.slug === agg.attribute) : undefined
    if (a?.config) out[`${agg.fn}.${agg.attribute}`] = a.config as Record<string, unknown>
  }
  return out
}

/** First FORWARD entity-reference on the widget's type whose target is the
 *  pivot. Reverse references (config.is_reverse) are projections, not paths —
 *  the reduce grammar can't traverse them. */
export function findForwardRefSlug(attrs: WidgetAttr[], pivotTypeId: string): string | undefined {
  return attrs.find(
    (a) =>
      a.type === 'entity-reference' &&
      (a.config as { is_reverse?: boolean } | null | undefined)?.is_reverse !== true &&
      a.target_type_id === pivotTypeId,
  )?.slug
}

export type MasterApplication =
  | { kind: 'apply'; tree: WireTree }
  | { kind: 'na' }
  | { kind: 'pending' }
  | { kind: 'none' }
  /** GAP D: the widget DECLARED that the page's scope does not apply to it.
   *  Distinct from 'na' (no path exists) and 'none' (no master filter) — the
   *  tile says which of the three it is, so an ignored scope is never silent. */
  | { kind: 'ignored' }

export function translateMaster(
  masterWire: WireTree | undefined,
  pivotTypeId: string | undefined,
  widgetTypeId: string,
  widgetAttrs: WidgetAttr[] | undefined,
): MasterApplication {
  if (masterWire === undefined || pivotTypeId === undefined) return { kind: 'none' }
  if (widgetTypeId === pivotTypeId) return { kind: 'apply', tree: masterWire }
  // Attributes not loaded yet: hold — rendering unfiltered would flash the
  // very silence the n/a badge exists to prevent.
  if (widgetAttrs === undefined) return { kind: 'pending' }
  const refSlug = findForwardRefSlug(widgetAttrs, pivotTypeId)
  if (refSlug === undefined) return { kind: 'na' }
  return { kind: 'apply', tree: prefixLeaves(masterWire, refSlug) }
}

/** GAP D (Task 7): translateMaster, plus the widget's own opt-out. ONE
 *  function so the tile's request and the tile's badge can never disagree
 *  about what was applied. */
export function applicationFor(
  config: Record<string, unknown> | undefined,
  masterWire: WireTree | undefined,
  pivotTypeId: string | undefined,
  widgetTypeId: string,
  widgetAttrs: WidgetAttr[] | undefined,
): MasterApplication {
  if (masterWire === undefined || pivotTypeId === undefined) return { kind: 'none' }
  if ((config ?? {}).master_filter === 'ignore') return { kind: 'ignored' }
  return translateMaster(masterWire, pivotTypeId, widgetTypeId, widgetAttrs)
}

/** AND-compose the widget's own config.filter with the translated master. */
export function composeFilter(widgetFilter: unknown, master: WireTree | undefined): unknown {
  if (master === undefined) return widgetFilter
  if (widgetFilter === undefined || widgetFilter === null) return master
  return { and: [widgetFilter, master] }
}

/** GAP D: every attribute slug the master filter names. They exist on the
 *  pivot type (the strip compiles against it) and are dim-projected (they
 *  are being filtered on), which makes them exactly the columns an identity
 *  reduce needs to DISCOVER the records the filter selects. */
export function pivotColumns(tree: WireTree): string[] {
  if (isWireLeaf(tree)) return [tree.attribute]
  const kids = 'and' in tree ? tree.and : tree.or
  return [...new Set(kids.flatMap(pivotColumns))]
}

/** GAP D: "Which records does the master filter select?" — asked of the
 *  ENGINE, never evaluated in the browser (the calculation-layer rule). The
 *  answer is the set of record ids a `highlight_pivot` table marks. */
export function pivotProbeRequest(entityDef: string, masterWire: WireTree): ReduceRequest {
  return {
    op: 'reduce',
    scope: { entity_def: entityDef, filter: masterWire as Scope['filter'] },
    reducer: { kind: 'identity', columns: pivotColumns(masterWire) },
    mrl_snapshot_id: null,
    accurate_at: null,
  }
}

/** Complete wire leaves — the "Master filter · N conditions" chip count. */
export function countLeaves(tree: WireTree | undefined): number {
  if (!tree) return 0
  if (isWireLeaf(tree)) return 1
  const children = 'and' in tree ? tree.and : tree.or
  return children.reduce((n, c) => n + countLeaves(c), 0)
}

/** Default pivot = the most common type among the current VIEW widgets. */
export function mostCommonTypeId(widgets: DashboardWidget[]): string | undefined {
  const counts = new Map<string, number>()
  for (const w of widgets) {
    if (w.view_kind === 'text' || !w.type_id) continue
    counts.set(w.type_id, (counts.get(w.type_id) ?? 0) + 1)
  }
  let best: string | undefined
  let n = 0
  for (const [id, c] of counts) if (c > n) { best = id; n = c }
  return best
}

// ── UI-tree round trip ───────────────────────────────────────────────────────
let _seq = 0
const freshUid = () => { _seq += 1; return `mf${_seq}` }

/** Rebuild a FilterBar GroupNode from the persisted `ui` key, reassigning
 *  transient _uids (they are React keys; persisting them would collide with
 *  the live editor's counter). Malformed input → undefined (fresh root). */
export function rehydrateUiTree(ui: unknown): GroupNode | undefined {
  const node = walkUi(ui)
  return node && node.kind === 'group' ? node : undefined
}

function walkUi(n: unknown): FilterNode | undefined {
  if (!n || typeof n !== 'object') return undefined
  const o = n as Record<string, unknown>
  if (o.kind === 'group' && Array.isArray(o.children)) {
    return {
      _uid: freshUid(),
      kind: 'group',
      conjunction: o.conjunction === 'or' ? 'or' : 'and',
      children: (o.children as unknown[]).map(walkUi).filter((c): c is FilterNode => c !== undefined),
    }
  }
  if (o.kind === 'condition' && typeof o.attributeId === 'string') {
    return { ...o, _uid: freshUid() } as FilterNode
  }
  return undefined
}

// ---------------------------------------------------------------------------
// Wire → UI tree (2026-09-02). An agent-written master filter carries the
// reduce wire only (`{attribute, op, value}`), no `ui` snapshot. The inline
// strip must still SHOW it as chips — and must never mistake "I cannot draw
// this" for "there is no filter" (the first inline cut did exactly that and
// cleared the Site page's scope on mount). Single-hop leaves on the pivot
// type rehydrate; anything else returns undefined and the strip shows the
// binding read-only instead.
// ---------------------------------------------------------------------------
import type { FilterCondition, FilterGroup } from '../collection/filter/model'

const UI_OP: Record<string, string> = { eq: 'eq', ne: 'neq', is_not_set: 'empty', is_set: 'non-empty', contains: 'contains' }

export function uiTreeFromWire(
  wire: WireTree | undefined,
  pivotTypeId: string,
  attrs: { attribute_id: string; slug: string; type: string; name?: string; filter_operators?: string[] | null }[],
): GroupNode | undefined {
  if (!wire) return undefined
  const leaf = (w: WireLeaf): FilterCondition | undefined => {
    if (w.attribute.includes('.')) return undefined   // a drilled path is not a pivot-type chip
    const a = attrs.find((x) => x.slug === w.attribute)
    if (!a) return undefined
    const operator = UI_OP[w.op]
    if (!operator) return undefined
    return {
      _uid: freshUid(), kind: 'condition', attributeId: a.attribute_id, attributeType: a.type,
      operator: operator as FilterCondition['operator'], value: w.value ?? undefined,
      attributeName: a.name, filter_operators: a.filter_operators ?? null,
      path: [{ type_id: pivotTypeId, attribute_id: a.attribute_id }],
    }
  }
  const walk = (w: WireTree): FilterNode | undefined => {
    if (isWireLeaf(w)) return leaf(w)
    const conj = 'and' in w ? 'and' : 'or'
    const kids = ('and' in w ? w.and : w.or).map(walk)
    if (kids.some((k) => k === undefined)) return undefined
    return { _uid: freshUid(), kind: 'group', conjunction: conj, children: kids as FilterNode[] } as FilterGroup
  }
  const node = walk(wire)
  if (!node) return undefined
  if (node.kind === 'group') return node as GroupNode
  return { _uid: freshUid(), kind: 'group', conjunction: 'and', children: [node] } as GroupNode
}
